/**
 * Element rendered at `/:tenantSlug/login` — the BRANDED login page.
 *
 * Three pre-render checks:
 *
 * 1. **Tenant must exist.** If the typed slug doesn't resolve to any
 *    tenant in the database, render a generic 404 page — NOT the
 *    default-branded login. The platform deliberately has no
 *    tenant-less public login: every login URL must correspond to a
 *    real tenant. Without this, `/random-slug/login` would let any
 *    real user submit credentials and be authenticated to whichever
 *    tenant their email lives in.
 *
 * 2. **Sub-tenant collapse**: if the URL slug IS a real tenant but
 *    rolls up to a different company-flagged ancestor, redirect to
 *    the company's branded login.
 *
 * 3. **Already-authenticated redirect**: if signed in AND the URL
 *    slug matches THEIR tenant, send them home. If the slug differs
 *    (multi-tenant: pasted another tenant's login URL while signed
 *    in to your own), render the form so they can authenticate as a
 *    different user. Per-tab sessionStorage keeps the other tab's
 *    session intact.
 *
 * The brand is resolved by BrandResolver from the URL slug.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoginPage } from '@modules/auth';
import { useAuth } from '@modules/auth';
import { buildUserHomePath } from './paths';
import { lookupTenantSlug, resolveTransactionalEmailForSlug, type SlugLookupResult } from './tenantResolver';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { TenantNotFound } from './TenantNotFound';

export function PublicTenantLogin() {
  const navigate = useNavigate();
  const { tenantSlug = '' } = useParams<{ tenantSlug?: string }>();
  const slug = tenantSlug.toLowerCase();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Look up the slug against the backend. We need three pieces of
  // information out of this: (a) does it exist at all? (b) what is
  // its canonical company ancestor? (c) is this the canonical or a
  // sub-tenant? Until the lookup resolves we render a LoadingScreen
  // so we never paint the wrong thing.
  const [lookup, setLookup] = useState<SlugLookupResult | null>(null);

  // Whether the branded login should show the "forgot password?" link.
  // Default TRUE and only flip to false once we POSITIVELY resolve the
  // tenant's `auth.transactional_email` flag to false — never hide during
  // load or on an unknown/failed lookup.
  const [forgotPasswordEnabled, setForgotPasswordEnabled] = useState(true);

  useEffect(() => {
    if (!slug) { setLookup({ status: 'not_found' }); return; }
    let cancelled = false;
    setLookup(null);
    (async () => {
      const r = await lookupTenantSlug(slug);
      if (!cancelled) setLookup(r);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const canonical = lookup?.status === 'found'
    ? (lookup.data.company_tenant_code || lookup.data.canonical_slug || '').toLowerCase()
    : '';

  // Sub-tenant → company redirect (only fires for FOUND slugs).
  useEffect(() => {
    if (lookup?.status !== 'found') return;
    if (canonical && canonical !== slug) {
      navigate(`/${canonical}/login`, { replace: true });
    }
  }, [lookup, canonical, slug, navigate]);

  // Resolve the login surface's `auth.transactional_email` flag so the
  // form can hide the "forgot password?" link for tenants where password
  // reset emails are disabled. Only runs once the slug is a FOUND tenant
  // that we'll actually render the login for (not a sub-tenant about to
  // redirect). We key off the canonical company slug — that's the tenant
  // whose users authenticate here. Unknown/failed stays enabled.
  useEffect(() => {
    if (lookup?.status !== 'found') return;
    if (canonical && canonical !== slug) return; // sub-tenant → redirecting
    const target = canonical || slug;
    if (!target) return;
    let cancelled = false;
    resolveTransactionalEmailForSlug(target).then((flag) => {
      if (cancelled) return;
      setForgotPasswordEnabled(flag !== false);
    });
    return () => { cancelled = true; };
  }, [lookup, canonical, slug]);

  // Already-authenticated handling — only auto-redirect when the
  // URL slug matches the signed-in user's own tenant. Otherwise we
  // render the form so they can sign in as a different user.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    if (lookup?.status !== 'found' || canonical !== slug) return;
    const userSlug = (user.company_tenant_code || user.tenant_code || '').toLowerCase();
    if (slug && userSlug && slug === userSlug) {
      navigate(buildUserHomePath(user), { replace: true });
    }
  }, [isAuthenticated, isLoading, user, slug, lookup, canonical, navigate]);

  // Lookup in flight — render loader; never paint wrong-brand login.
  if (lookup === null) return <LoadingScreen />;
  // Tenant doesn't exist — render the generic 404 page. Network
  // errors (status === 'error') also fall through to the not-found
  // page so a slug we can't verify never serves the login form;
  // refreshing after the network recovers will re-attempt.
  if (lookup.status !== 'found') return <TenantNotFound />;
  // About to redirect to the company URL — render loader to avoid a
  // one-frame flash of the typed slug's login page.
  if (canonical && canonical !== slug) return <LoadingScreen />;

  return <LoginPage forgotPasswordEnabled={forgotPasswordEnabled} />;
}
